// Peças que as duas páginas com dados da DRE repartem (aviso com link, tooltip do gráfico,
// estado de carregando/erro/sem acesso). Sem regra de negócio: as contas ficam em dadosSetores.ts.
import { useMemo, useState, type ReactNode } from 'react';
import { Alert, Button, Checkbox, Select, Space, Spin, Switch, Tooltip as TipAntd } from 'antd';
import { Link } from 'react-router-dom';
import { metaSetor } from '../../../utils/setoresDre';
import { hojeIso } from '../../../utils/dataFmt';
import {
  janelaDoFiltro, opcoesDeMeses, reais, type FiltroPeriodo, type JanelaFiltro, type ModoPeriodo,
} from './dadosSetores';

export const LINK_AREAS = '/operacional/contratos?vista=areas';
export const LINK_LANCAMENTOS = '/financeiro/dre-hub?aba=lancamentos';
export const LINK_MAO_OBRA = '/financeiro/dre-hub?aba=mao-obra';

/** Link interno com texto de menu ("Operacional › Contratos › Áreas e CNAE"). */
export function IrPara({ para, children }: { para: string; children: ReactNode }) {
  return <Link to={para}>{children}</Link>;
}

export function SemAcesso({ texto }: { texto?: string }) {
  return (
    <Alert
      type="warning" showIcon
      message="Você não tem acesso a esta página"
      description={texto || 'Os números desta página são do financeiro. Peça acesso ao Lucas se precisar deles.'}
    />
  );
}

/** Quando a lista de acessos não carregou (rede): não é o mesmo que "sem acesso". */
export function AcessoNaoConferido({ onTentar }: { onTentar: () => void }) {
  return (
    <Alert
      type="warning" showIcon
      message="Não consegui conferir o seu acesso"
      description="Foi um problema de conexão, não falta de permissão. Tente de novo em instantes."
      action={<Button size="small" onClick={onTentar}>Tentar de novo</Button>}
    />
  );
}

export function Carregando({ texto }: { texto?: string }) {
  return (
    <div style={{ padding: 48, textAlign: 'center' }}>
      <Spin size="large" />
      {texto ? <div style={{ marginTop: 12, color: '#8c8c8c' }}>{texto}</div> : null}
    </div>
  );
}

/**
 * Tooltip de gráfico empilhado: uma linha por setor (só os que têm valor), o total da barra
 * (a inclinação da tendência fica só na legenda do gráfico, para o número não aparecer duas vezes).
 */
export function TooltipEmpilhado({ active, payload, label, rotuloTotal, umaBarra }: {
  active?: boolean; payload?: any[]; label?: string; rotuloTotal: string; umaBarra?: boolean;
}) {
  if (!active || !payload || !payload.length) return null;
  const linha = payload[0]?.payload || {};
  const itens = payload
    .filter((p) => p.dataKey !== 'total' && p.dataKey !== 'tendencia' && Math.abs(Number(p.value) || 0) >= 0.5)
    .sort((a, b) => (Number(b.value) || 0) - (Number(a.value) || 0));
  const total = Number(linha.total) || 0;
  // Uma barra só (setor escolhido): o total repetiria o mesmo número.
  const mostrarTotal = !umaBarra;
  return (
    <div style={{ background: '#fff', border: '1px solid #d9d9d9', borderRadius: 6, padding: '8px 12px', fontSize: 12, boxShadow: '0 2px 8px rgba(0,0,0,.12)' }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}{linha.parcial ? ' (mês em andamento)' : ''}</div>
      {itens.map((p) => (
        <div key={String(p.dataKey)} style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, background: p.color || p.fill, marginRight: 6, borderRadius: 2 }} />{metaSetor(String(p.dataKey)).rotulo}</span>
          <span style={{ color: Number(p.value) < 0 ? '#a8071a' : undefined }}>{reais(Number(p.value))}</span>
        </div>
      ))}
      {mostrarTotal ? (
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, borderTop: '1px solid #f0f0f0', marginTop: 4, paddingTop: 4, fontWeight: 600 }}>
          <span>{rotuloTotal}</span><span>{reais(total)}</span>
        </div>
      ) : null}
    </div>
  );
}

/** Cor da linha de tendência: neutra (cor de texto), tracejada, nunca a cor de um setor. */
export const COR_TENDENCIA = '#595959';
export const TRACO_TENDENCIA = '6 4';

// ── Barra de filtros (período, setor, tendência) ─────────────────────────────
const OPCOES_PERIODO: { value: ModoPeriodo; label: string }[] = [
  { value: 'ultimos12', label: 'Últimos 12 meses' },
  { value: 'este_ano', label: 'Este ano' },
  { value: 'ano_passado', label: 'Ano passado' },
  { value: 'personalizado', label: 'Personalizado' },
];

/** Estado do filtro de período + a janela que ele dá (de/ate para a rota, com aviso). */
export function usePeriodo(hojeProp: string | undefined, maxMeses: number) {
  const [hojeLocal] = useState(() => hojeIso());
  const hoje = hojeProp || hojeLocal;
  const [filtro, setFiltro] = useState<FiltroPeriodo>({ modo: 'ultimos12', incluirAtual: false });
  const janela: JanelaFiltro = useMemo(() => janelaDoFiltro(filtro, hoje, maxMeses), [filtro, hoje, maxMeses]);
  return { hoje, filtro, setFiltro, janela };
}

/** Período: Últimos 12 · Este ano · Ano passado · Personalizado (de/até) + "incluir mês atual". */
export function FiltroDePeriodo({ filtro, onChange, hoje, janela }: {
  filtro: FiltroPeriodo; onChange: (f: FiltroPeriodo) => void; hoje: string; janela: JanelaFiltro;
}) {
  const meses = useMemo(() => opcoesDeMeses(hoje), [hoje]);
  const trocarModo = (modo: ModoPeriodo) => {
    // Personalizado começa no período que já está na tela.
    if (modo === 'personalizado' && janela.ok) onChange({ ...filtro, modo, de: janela.de, ate: janela.ate });
    else onChange({ ...filtro, modo });
  };
  const propsMes = { showSearch: true, optionFilterProp: 'label', options: meses, style: { width: 130 }, popupMatchSelectWidth: false };
  return (
    <Space size={8} wrap>
      <Select
        aria-label="Período" value={filtro.modo} onChange={trocarModo} options={OPCOES_PERIODO}
        showSearch optionFilterProp="label" style={{ width: 170 }}
      />
      {filtro.modo === 'personalizado' ? (
        <>
          <Select {...propsMes} aria-label="Mês inicial" placeholder="De" value={filtro.de || undefined} onChange={(v: string) => onChange({ ...filtro, de: v })} />
          <Select {...propsMes} aria-label="Mês final" placeholder="Até" value={filtro.ate || undefined} onChange={(v: string) => onChange({ ...filtro, ate: v })} />
        </>
      ) : null}
      <TipAntd title={filtro.modo === 'ano_passado' ? 'O ano passado já fechou inteiro.' : 'O mês em andamento aparece menor do que vai fechar.'}>
        <Checkbox
          checked={filtro.incluirAtual && filtro.modo !== 'ano_passado'} disabled={filtro.modo === 'ano_passado'}
          onChange={(e) => onChange({ ...filtro, incluirAtual: e.target.checked })}
        >
          incluir mês atual
        </Checkbox>
      </TipAntd>
    </Space>
  );
}

/** Select de setor com busca: "Todos os setores" ou um (com a bolinha da cor fixa dele). */
export function FiltroDeSetor({ setores, valor, onChange }: { setores: string[]; valor: string | null; onChange: (s: string | null) => void }) {
  const opcoes = [
    { value: '__todos', label: 'Todos os setores' },
    ...setores.map((s) => ({ value: s, label: metaSetor(s).rotulo })),
  ];
  return (
    <Select
      aria-label="Setor" value={valor && setores.includes(valor) ? valor : '__todos'}
      onChange={(v: string) => onChange(v === '__todos' ? null : v)}
      options={opcoes} showSearch optionFilterProp="label" style={{ width: 230 }} popupMatchSelectWidth={false}
      optionRender={(o) => (o.value === '__todos' ? o.label : <EtiquetaSetor chave={String(o.value)} />)}
    />
  );
}

/** Switch "Linha de tendência". */
export function FiltroDeTendencia({ ligado, onChange, desligadoPorque }: {
  ligado: boolean; onChange: (v: boolean) => void; desligadoPorque?: string | null;
}) {
  const sw = (
    <Space size={6}>
      <Switch size="small" checked={ligado && !desligadoPorque} disabled={!!desligadoPorque} onChange={onChange} aria-label="Linha de tendência" />
      <span style={{ color: desligadoPorque ? '#bfbfbf' : undefined }}>Linha de tendência</span>
    </Space>
  );
  return desligadoPorque ? <TipAntd title={desligadoPorque}>{sw}</TipAntd> : sw;
}

/** Bolinha colorida do setor + rótulo (legenda das tabelas, com a mesma cor do gráfico). */
export function EtiquetaSetor({ chave, quebrar = false }: { chave: string; quebrar?: boolean }) {
  const m = metaSetor(chave);
  // quebrar: título de coluna de largura fixa (15/10/2026) — o nome comprido desce de linha em vez de cortar
  return (
    <span style={{ whiteSpace: quebrar ? 'normal' : 'nowrap' }}>
      <span style={{ display: 'inline-block', width: 10, height: 10, background: m.cor, marginRight: 6, borderRadius: 2, border: m.chave === 'sem_setor' ? '1px solid #bfbfbf' : undefined }} />
      {m.rotulo}
    </span>
  );
}
